import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Task1Type, VisualType, QuestionType } from '@/types/task-bank';
import { Tag } from '@/app/task-bank/public-actions';

interface TaskFiltersProps {
  taskType: 'task1' | 'task2';
  searchQuery: string;
  onSearchChange: (value: string) => void;

  // Task 1 filters
  task1TypeFilter: Task1Type | 'all';
  onTask1TypeChange: (value: Task1Type | 'all') => void;
  visualTypeFilter: VisualType | 'all';
  onVisualTypeChange: (value: VisualType | 'all') => void;

  // Task 2 filters
  questionTypeFilter: QuestionType | 'all';
  onQuestionTypeChange: (value: QuestionType | 'all') => void;

  // Keywords (Task 2 only)
  keywords: Tag[];
  selectedKeywordId: string | null;
  onToggleKeyword: (id: string) => void;
  showAllKeywords: boolean;
  onToggleShowAllKeywords: (show: boolean) => void;
  keywordSearch: string;
  onKeywordSearchChange: (value: string) => void;
}

export function TaskFilters({
  taskType,
  searchQuery,
  onSearchChange,
  task1TypeFilter,
  onTask1TypeChange,
  visualTypeFilter,
  onVisualTypeChange,
  questionTypeFilter,
  onQuestionTypeChange,
  keywords,
  selectedKeywordId,
  onToggleKeyword,
  showAllKeywords,
  onToggleShowAllKeywords,
  keywordSearch,
  onKeywordSearchChange,
}: TaskFiltersProps) {
  const filteredKeywords = keywordSearch.trim()
    ? keywords.filter((kw) =>
        kw.name.toLowerCase().includes(keywordSearch.trim().toLowerCase())
      )
    : keywords;

  const visibleKeywords = showAllKeywords
    ? filteredKeywords
    : filteredKeywords.slice(0, 10);

  return (
    <div className='flex flex-col space-y-2'>
      <div className='flex items-center space-x-2'>
        <Input
          placeholder='Search tasks...'
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className='flex-1'
        />
      </div>

      {/* Keyword filter - Only show for Task 2 */}
      {taskType === 'task2' && (
        <div className='border-t pt-2 pb-2'>
          <span className='text-xs font-medium mr-2'>Filter by Keyword:</span>
          <Input
            value={keywordSearch}
            onChange={(e) => onKeywordSearchChange(e.target.value)}
            placeholder='Search keywords...'
            className='text-xs mb-2 w-full'
          />
          <div
            className={`flex flex-wrap gap-1 mb-2 ${
              showAllKeywords ? 'max-h-40 overflow-y-auto' : ''
            }`}
          >
            {visibleKeywords.map((kw) => (
              <Badge
                key={kw.id}
                variant={selectedKeywordId === kw.id ? 'default' : 'secondary'}
                onClick={() => onToggleKeyword(kw.id)}
                className='cursor-pointer'
              >
                {kw.name}
              </Badge>
            ))}
            {filteredKeywords.length === 0 && (
              <span className='text-xs text-muted-foreground'>
                No keywords found.
              </span>
            )}
          </div>
          {filteredKeywords.length > 10 && (
            <Button
              type='button'
              size='sm'
              variant='ghost'
              className='text-xs px-2 py-1 h-7'
              onClick={() => onToggleShowAllKeywords(!showAllKeywords)}
            >
              {showAllKeywords
                ? 'Show Less'
                : `Show More (${filteredKeywords.length - 10})`}
            </Button>
          )}
        </div>
      )}

      {/* Task type specific filters */}
      <div className='flex space-x-2 border-t pt-2'>
        {taskType === 'task1' ? (
          <>
            <select
              value={task1TypeFilter}
              onChange={(e) =>
                onTask1TypeChange(e.target.value as Task1Type | 'all')
              }
              className='rounded-md border border-input bg-background px-3 py-1 text-sm'
            >
              <option value='all'>All types</option>
              <option value='dynamic'>Dynamic</option>
              <option value='static'>Static</option>
              <option value='diagram'>Diagram</option>
              <option value='map'>Map</option>
            </select>

            <select
              value={visualTypeFilter}
              onChange={(e) =>
                onVisualTypeChange(e.target.value as VisualType | 'all')
              }
              className='rounded-md border border-input bg-background px-3 py-1 text-sm'
            >
              <option value='all'>All visuals</option>
              <option value='line_graph'>Line Graph</option>
              <option value='bar_chart'>Bar Chart</option>
              <option value='pie_chart'>Pie Chart</option>
              <option value='table'>Table</option>
              <option value='mixed'>Mixed</option>
            </select>
          </>
        ) : (
          <select
            value={questionTypeFilter}
            onChange={(e) =>
              onQuestionTypeChange(e.target.value as QuestionType | 'all')
            }
            className='rounded-md border border-input bg-background px-3 py-1 text-sm'
          >
            <option value='all'>All question types</option>
            <option value='discussion'>Discussion</option>
            <option value='opinion'>Opinion</option>
            <option value='problem_solution'>Problem Solution</option>
            <option value='advantage_disadvantage'>
              Advantage Disadvantage
            </option>
            <option value='direct_question'>Direct Question</option>
            <option value='two_part_question'>Two-part Question</option>
          </select>
        )}
      </div>
    </div>
  );
}
